import React, { FC, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'gatsby-plugin-react-i18next'

interface SearchItem {
  title: string
  category: string
  url: string
  description?: string
}

const SEARCH_ITEMS: SearchItem[] = [
  { title: 'introduction', category: 'documentation', url: '/design-system/docs#introduction', description: 'overview me&my friends ui design system' },
  { title: 'installation & quick start', category: 'documentation', url: '/design-system/docs#installation', description: 'cli setup and tailwind configuration' },
  { title: 'copy & paste architecture', category: 'documentation', url: '/design-system/docs#architecture', description: 'component code ownership architecture' },
  { title: 'figma variables plugin', category: 'documentation', url: '/design-system/docs#figma-plugin', description: 'automated figma tokens and color scales' },
  { title: 'accessibility standards', category: 'documentation', url: '/design-system/docs#accessibility', description: 'wcag aaa contrast and accessibility tokens' },
  { title: 'accordion', category: 'components', url: '/design-system/components/accordion', description: 'vertically stacked collapsible headings' },
]

export const DesignSystemSearch: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsOpen((prev) => !prev)
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  useEffect(() => {
    const lenis = (window as any).lenis
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      if (lenis && typeof lenis.stop === 'function') {
        lenis.stop()
      }
    } else {
      document.body.style.overflow = ''
      if (lenis && typeof lenis.start === 'function') {
        lenis.start()
      }
    }
  }, [isOpen])

  const filteredResults = query.trim() === ''
    ? SEARCH_ITEMS
    : SEARCH_ITEMS.filter(
        (item) =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          (item.description && item.description.toLowerCase().includes(query.toLowerCase())) ||
          item.category.toLowerCase().includes(query.toLowerCase())
      )

  const modalContent = isOpen ? (
    <div
      className="fixed inset-0 z-[99999] flex items-start justify-center pt-16 sm:pt-24 px-4 bg-black/75 transition-opacity"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-2xl overflow-hidden flex flex-col max-h-[80vh] data-lenis-prevent"
        onClick={(e) => e.stopPropagation()}
        data-lenis-prevent
      >
        {/* Search Input Bar */}
        <div className="p-4 border-b border-neutral-200/80 dark:border-neutral-800 flex items-center gap-3">
          <svg className="w-5 h-5 text-neutral-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>

          <input
            type="text"
            autoFocus
            placeholder="search documentation and components..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-transparent text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none lowercase font-inter"
          />

          <button
            onClick={() => setIsOpen(false)}
            className="px-2 py-1 text-xs text-neutral-400 hover:text-neutral-700 dark:hover:text-white rounded-lg bg-neutral-100 dark:bg-neutral-800"
          >
            esc
          </button>
        </div>

        {/* Results List */}
        <div className="p-4 overflow-y-auto space-y-2 overscroll-contain flex-1" data-lenis-prevent>
          {filteredResults.length === 0 ? (
            <div className="p-8 text-center text-xs text-neutral-400 font-inter lowercase">
              no results found for &quot;{query}&quot;
            </div>
          ) : (
            filteredResults.map((item, index) => (
              <Link
                key={index}
                to={item.url}
                onClick={() => setIsOpen(false)}
                className="block p-3 rounded-2xl hover:bg-neutral-100 dark:hover:bg-neutral-800/60 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-neutral-900 dark:text-white group-hover:text-red transition-colors lowercase">
                    {item.title}
                  </span>
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-400">
                    {item.category}
                  </span>
                </div>
                {item.description && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 font-inter lowercase line-clamp-1">
                    {item.description}
                  </p>
                )}
              </Link>
            ))
          )}
        </div>

        {/* Footer info */}
        <div className="p-3 border-t border-neutral-200/80 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 flex items-center justify-between text-[11px] text-neutral-400 font-mono">
          <span>search design system</span>
          <div className="flex items-center gap-2">
            <span>navigate with keyboard</span>
            <span className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-800 rounded">⌘K</span>
          </div>
        </div>
      </div>
    </div>
  ) : null

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="min-h-[48px] px-6 md:px-8 py-4 font-normal text-base rounded-full border-2 border-transparent bg-mediumLightGray dark:bg-white/5 text-secondaryGray700 dark:text-neutral-300 hover:border-red/50 hover:bg-red/5 transition-all flex items-center gap-3 lowercase"
        aria-label="search documentation"
      >
        <span>search documentation</span>
        <span className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-neutral-200/70 dark:bg-white/10 rounded text-neutral-500 dark:text-neutral-400">
          ⌘K
        </span>
      </button>

      {mounted && createPortal(modalContent, document.body)}
    </>
  )
}

export default DesignSystemSearch
